import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { telHref } from '../lib/supportContact';
import { usePortalSiteStore } from '../stores/portalSiteStore';

interface AccountPendingNoticeProps {
	/**
	 * Tighter spacing for the mobile pinned Place Order bar, which sits over
	 * the page and should stay as short as it can.
	 */
	compact?: boolean;
}

// Placeholders handed to t() in place of the phone and email, so the whole
// sentence is translated as one string and the links are put back where the
// translation placed them.
const PHONE_TOKEN = '[[phone]]';
const EMAIL_TOKEN = '[[email]]';
const TOKEN_PATTERN = /(\[\[phone\]\]|\[\[email\]\])/;

/**
 * PD-13362 — shown above Place Order while the active account is a lead (not
 * yet a customer account). The buyer can keep shopping and fill in checkout;
 * only placing the order is refused. The wording asks them to contact the
 * distributor, since nothing is necessarily under way for a lead (a waiting
 * message would imply it is). The support line appears only when
 * the brand has a customer-support phone or email, with each one a real
 * tel: / mailto: link like the rest of the storefront's support surfaces.
 */
export function AccountPendingNotice({
	compact = false,
}: AccountPendingNoticeProps): JSX.Element {
	const { t } = useTranslation();
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	const phone = supportContact?.phone ?? null;
	const email = supportContact?.email ?? null;

	let supportLine: ReactNode = null;
	if (phone != null || email != null) {
		let sentence: string;
		if (phone != null && email != null) {
			sentence = t('Questions? Call {{phone}} or email {{email}}.', {
				phone: PHONE_TOKEN,
				email: EMAIL_TOKEN,
			});
		} else if (phone != null) {
			sentence = t('Questions? Call {{phone}}.', { phone: PHONE_TOKEN });
		} else {
			sentence = t('Questions? Email {{email}}.', { email: EMAIL_TOKEN });
		}

		supportLine = sentence.split(TOKEN_PATTERN).map((part, index) => {
			if (part === PHONE_TOKEN && phone != null) {
				return (
					<a
						key={`phone-${index}`}
						href={telHref(phone)}
						className="font-semibold underline"
					>
						{t(phone)}
					</a>
				);
			}
			if (part === EMAIL_TOKEN && email != null) {
				return (
					<a
						key={`email-${index}`}
						href={`mailto:${email}`}
						className="font-semibold underline break-all"
					>
						{t(email)}
					</a>
				);
			}
			return <span key={`text-${index}`}>{part}</span>;
		});
	}

	return (
		<div
			role="status"
			className={`rounded-xl border bg-warning-badge text-warning-tint-fg border-warning-tint ${
				compact ? 'mb-2 px-3 py-2 text-xs' : 'mb-3 p-3 text-sm'
			}`}
		>
			<div className="flex items-start gap-2">
				<FontAwesomeIcon icon={faCircleInfo} className="text-warning mt-0.5" />
				<div className="min-w-0">
					<p className="font-semibold">{t('Contact us to start ordering')}</p>
					<p className="mt-0.5">
						{t(
							'You can keep adding items to your cart. To place an order, contact us to set up your account.',
						)}
					</p>
					{supportLine != null && <p className="mt-1">{supportLine}</p>}
				</div>
			</div>
		</div>
	);
}
